/**
 * The Platform Core capabilities a module may declare a dependency on.
 *
 * COMMERCIAL_MODULARITY §3: "the registry rejects a definition whose required
 * Platform Core capability does not exist". That check needs a list of what
 * exists, and this is it — taken from §2.1, which enumerates Platform Core.
 *
 * A capability appearing here is a statement that Platform Core owns it, not
 * that Stage 2 has built it. The dependency declaration exists so that "who
 * consumes the numbering kernel?" is answerable before the numbering kernel has
 * consumers, rather than discovered by breaking them.
 */
export const CORE_CAPABILITIES = [
    // The kernel packages named in §2.1.
    'tenancy',
    'hierarchy',
    'identity',
    'authz',
    'audit',
    'notifications',
    'approvals',
    'storage',
    'attachments',
    'search',
    'entitlements',
    'ai_gateway',
    'config',
    // The supporting kernel services.
    'events',
    'jobs',
    'numbering',
    'money',
    'tax',
    'i18n',
    'comments',
    'tasks',
    'reporting',
    'observability',
    // The internal bounded contexts — explicitly not SKUs (D-1, §2.1).
    //
    // TWO OF THESE CARRY A `_kernel` SUFFIX AND THE SUFFIX IS LOAD-BEARING.
    // Procurement and Compliance & Risk are approved Phase 1 SKUs, and
    // `validateRegistry` refuses a sellable module key that collides with a
    // capability name — a `requiredCore: ['procurement']` would otherwise be
    // ambiguous between the mandatory Core context every tenant has and a SKU
    // the customer might not have bought (§5).
    //
    // Renaming the CAPABILITY rather than the module keeps the guarantee and
    // states it more plainly than before: Finance requires the purchasing
    // KERNEL, which is mandatory, and does not require the Procurement SKU,
    // which is optional. Four modules stand in the same relation to the
    // compliance kernel.
    'party_master',
    'procurement_kernel',
    'inventory',
    'workforce_ops',
    'compliance_kernel',
];
const KNOWN = new Set(CORE_CAPABILITIES);
export function isCoreCapability(value) {
    return KNOWN.has(value);
}
/**
 * The navigation modules a manifest may surface as.
 *
 * Design system v1.0 §7 froze twelve. The approved Phase 1 product model added
 * Procurement and Compliance & Risk, so there are now fourteen — the frozen
 * twelve plus those two. Navigation is still not something a module invents:
 * an entry that is not on this list is refused at registry load, exactly as
 * before. What changed is the list, by owner decision, not the rule.
 *
 * Membership here is NOT the same as appearing in the sidebar. Three entries —
 * `field-service`, `lms` and `ai-automation` — remain registered because their
 * manifests and their optional-dependency relationships remain real, and are
 * kept out of Phase 1 navigation by `commercialRelease` instead.
 */
export const NAVIGATION_MODULES = [
    'dashboard',
    'finance',
    'crm',
    'procurement',
    'field-service',
    'service-support',
    'hr',
    'projects',
    'documents',
    'compliance',
    'lms',
    'ai-automation',
    'reports',
    'administration',
];
const NAVIGATION = new Set(NAVIGATION_MODULES);
export function isNavigationModule(value) {
    return NAVIGATION.has(value);
}
//# sourceMappingURL=capabilities.js.map